import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import Drawer from '../components/Drawer';
import { useBusiness } from '../context/BusinessContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import { money, date } from '../lib/format';

const today = () => new Date().toISOString().slice(0, 10);

export default function Deposits() {
  const { activeId } = useBusiness();
  const { can } = useAuth();

  const [undeposited, setUndeposited] = useState([]);
  const [history, setHistory] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('new'); // new | view
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ bank_account_id: '', deposit_date: today() });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api(`/deposits/undeposited?business_id=${activeId}`).catch(() => []),
      api(`/deposits?business_id=${activeId}`).catch(() => []),
    ])
      .then(([u, h]) => {
        setUndeposited(Array.isArray(u) ? u : []);
        setHistory(Array.isArray(h) ? h : []);
        setSelected(new Set());
      })
      .catch(() => setError('Could not load deposits.'))
      .finally(() => setLoading(false));
  }, [activeId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api(`/accounts?business_id=${activeId}&type=asset`)
      .then((d) => setBanks((Array.isArray(d) ? d : []).filter((a) => a.is_bank)))
      .catch(() => setBanks([]));
  }, [activeId]);

  const selectedTotal = useMemo(
    () => undeposited.filter((p) => selected.has(p.id)).reduce((s, p) => s + Number(p.amount), 0),
    [undeposited, selected]
  );

  function toggle(id) {
    setSelected((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  function toggleAll() {
    setSelected((s) => (s.size === undeposited.length ? new Set() : new Set(undeposited.map((p) => p.id))));
  }

  function startDeposit() {
    setMode('new');
    setForm({ bank_account_id: banks[0]?.id || '', deposit_date: today() });
    setFormError(null);
    setOpen(true);
  }

  async function openDeposit(row) {
    setMode('view');
    setFormError(null);
    setOpen(true);
    try {
      setDetail(await api(`/deposits/${row.id}`));
    } catch {
      setDetail(null);
    }
  }

  async function createDeposit() {
    if (!form.bank_account_id) { setFormError('Choose a bank account.'); return; }
    setSaving(true);
    setFormError(null);
    try {
      await api('/deposits', {
        method: 'POST',
        body: {
          business_id: activeId,
          bank_account_id: Number(form.bank_account_id),
          deposit_date: form.deposit_date,
          payment_ids: [...selected],
        },
      });
      setOpen(false);
      load();
    } catch (e) {
      setFormError(e?.detail || 'Could not create the deposit.');
    } finally {
      setSaving(false);
    }
  }

  const historyColumns = [
    { key: 'deposit_date', header: 'Date', render: (r) => date(r.deposit_date) },
    { key: 'bank_account', header: 'Deposited to', render: (r) => r.bank_account || '—' },
    { key: 'payment_count', header: 'Payments', render: (r) => r.payment_count },
    { key: 'total', header: 'Total', render: (r) => <span className="num">{money(r.total)}</span> },
  ];

  const selectedPayments = undeposited.filter((p) => selected.has(p.id));

  return (
    <>
      <PageHeader
        title="Deposits"
        actions={
          can('deposits.create') && selected.size > 0 ? (
            <button className="btn btn--primary" onClick={startDeposit}>
              Deposit {selected.size} · {money(selectedTotal)}
            </button>
          ) : null
        }
      />

      <section className="panel">
        <div className="panel__title">Undeposited funds</div>
        {error && <div className="form-error" style={{ margin: 16 }}>{error}</div>}
        {loading ? (
          <div className="perm-empty">Loading…</div>
        ) : undeposited.length === 0 ? (
          <div className="perm-empty">Nothing waiting to be deposited.</div>
        ) : (
          <table className="table dep-table">
            <thead>
              <tr>
                <th className="dep-check">
                  <input type="checkbox" checked={selected.size === undeposited.length}
                    onChange={toggleAll} aria-label="Select all" />
                </th>
                <th>Date</th><th>Customer</th><th>Method</th><th>Reference</th>
                <th className="num-col">Amount</th>
              </tr>
            </thead>
            <tbody>
              {undeposited.map((p) => (
                <tr key={p.id} className="is-clickable" onClick={() => toggle(p.id)}>
                  <td className="dep-check">
                    <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)}
                      onClick={(e) => e.stopPropagation()} />
                  </td>
                  <td>{date(p.payment_date)}</td>
                  <td>{p.customer_name || '—'}</td>
                  <td className="cap">{p.method}</td>
                  <td>{p.reference || '—'}</td>
                  <td className="num num-col">{money(p.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="panel">
        <div className="panel__title">Deposit history</div>
        <DataTable
          columns={historyColumns}
          rows={history}
          loading={loading}
          getRowKey={(r) => r.id}
          onRowClick={openDeposit}
          empty="No deposits yet."
        />
      </section>

      <Drawer
        open={open}
        title={mode === 'new' ? 'Make a deposit' : 'Deposit'}
        onClose={() => !saving && setOpen(false)}
        footer={
          mode === 'new' ? (
            <>
              <button className="btn" onClick={() => setOpen(false)} disabled={saving}>Cancel</button>
              <button className="btn btn--primary" onClick={createDeposit} disabled={saving}>
                {saving ? 'Saving…' : 'Create deposit'}
              </button>
            </>
          ) : (
            <button className="btn" onClick={() => setOpen(false)}>Close</button>
          )
        }
      >
        {mode === 'new' ? (
          <>
            {formError && <div className="form-error">{formError}</div>}
            <div className="form-grid">
              <div className="field">
                <label>Deposit to</label>
                <select value={form.bank_account_id} onChange={(e) => setForm((f) => ({ ...f, bank_account_id: e.target.value }))}>
                  <option value="">Select bank account…</option>
                  {banks.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Date</label>
                <input type="date" value={form.deposit_date}
                  onChange={(e) => setForm((f) => ({ ...f, deposit_date: e.target.value }))} />
              </div>
            </div>
            <h3 className="form-section">Payments in this deposit</h3>
            <div className="pay-log">
              {selectedPayments.map((p) => (
                <div className="pay-row" key={p.id}>
                  <span>{date(p.payment_date)}</span>
                  <span className="pay-method">{p.customer_name || '—'}{p.reference ? ` · ${p.reference}` : ''}</span>
                  <span className="num">{money(p.amount)}</span>
                </div>
              ))}
            </div>
            <div className="pay-balance"><span>Deposit total</span><span className="num">{money(selectedTotal)}</span></div>
          </>
        ) : detail ? (
          <>
            <div className="inv-parties" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <div><div className="inv-parties__label">Deposited to</div><div className="inv-parties__name">{detail.bank_account}</div></div>
              <div><div className="inv-parties__label">Date</div><div className="ro-value">{date(detail.deposit_date)}</div></div>
            </div>
            <h3 className="form-section">Payments</h3>
            <div className="pay-log">
              {(detail.payments || []).map((p) => (
                <div className="pay-row" key={p.id}>
                  <span>{date(p.payment_date)}</span>
                  <span className="pay-method">{p.customer_name || '—'}{p.reference ? ` · ${p.reference}` : ''}</span>
                  <span className="num">{money(p.amount)}</span>
                </div>
              ))}
            </div>
            <div className="pay-balance"><span>Total</span><span className="num">{money(detail.total)}</span></div>
          </>
        ) : (
          <div className="perm-empty">Loading…</div>
        )}
      </Drawer>
    </>
  );
}
